import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServicesService } from 'src/services/services.service';
import { EncryptionService } from "src/services/encryption.service";

type DashboardTone = 'primary' | 'success' | 'warning' | 'danger' | 'info';

interface DashboardMetric {
  label: string;
  value: string;
  helper: string;
  icon: string;
  tone: DashboardTone;
}

interface ScannerActivity {
  user_name: string;
  user_mobile: string;
  date: string;
  checkpoint: string;
  totalScans: number;
  checkins: number;
  checkouts: number;
  last_scan_at: string;
  [key: string]: any;
}

interface TicketCategoryStat {
  category: string;
  total_reg: number;
  checkins: number;
  checkouts: number;
  not_visited: number;
  visitRate: number;
}

interface CheckpointCategoryStat {
  checkpoint: string;
  category: string;
  total_reg: number;
  checkins: number;
  not_visited: number;
  checkouts: number;
  uniqueVisitors: number;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardComponent {
  result: any;
  allcount: any;
  role: any;
  event_id: any;
  setting_data: any;

  total_users: number = 0;
  total_events: number = 0;
  past_events: number = 0;
  current_events: number = 0;
  future_events: number = 0;
  event_user: number = 0;
  total_admin: number = 0;

  registrations: number = 0;
  checkins: number = 0;
  checkouts: number = 0;
  pending_registrations: number = 0;
  totalNotVisited: number = 0;
  active_scanning_points: number = 0;
  active_participants: number = 0;
  registration: any = 0;

  scans_by_checkpoint: any[] = [];
  scanner_activity: ScannerActivity[] = [];
  ticket_category_attendance: TicketCategoryStat[] = [];
  checkpoint_category_scans: CheckpointCategoryStat[] = [];

  registrationTrendData: any = null;
  checkpointScansChartData: any = null;
  ticketCategoryAttendanceChartData: any = null;

  summaryMetrics: DashboardMetric[] = [];
  approvedRegistrations: number = 0;
  totalScannerActions: number = 0;
  registrationOpen: boolean = false;
  registrationStatusLabel: string = 'Closed';
  lastUpdated: Date | null = null;
  isLoading: boolean = false;

  searchTerm: string = '';
  filteredScannerActivity: any[] = [];
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private service: ServicesService,
    private router: Router,
    private route: ActivatedRoute,
    private encryptService: EncryptionService
  ) { }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const urlUser = params['User'];
      const urlUserId = params['User_ID'];

      if (urlUser && urlUserId) {
        sessionStorage.setItem('User', urlUser);
        sessionStorage.setItem('User_ID', urlUserId);

        this.router.navigateByUrl('/admin/Dashboard').then(() => {
          window.location.reload();
        });
      }
    });
    this.role = window.sessionStorage.getItem('User');
    this.event_id = window.sessionStorage.getItem('Event_ID');

    this.isLoading = true;
    this.Dashboard_Count(this.role);
    this.Get_Setting(this.event_id);


   const data = JSON.stringify({ is_active: 0 });
const encrypted = this.encryptService.encrypt(data);
console.log(encrypted);
  }

  get hasEventContext(): boolean {
    return !!this.event_id && this.event_id !== '0';
  }

  get canViewEventAnalytics(): boolean {
    return this.role === 'admin' || (this.role === 'superadmin' && this.hasEventContext) || this.role === 'subadmin';
  }

  get canViewSystemAnalytics(): boolean {
    return this.role === 'superadmin' && !this.hasEventContext;
  }

  get dashboardModeLabel(): string {
    if (this.canViewEventAnalytics) {
      return 'Registration and scanning overview';
    }

    if (this.canViewSystemAnalytics) {
      return 'Select an event for scan details';
    }

    if (this.role === 'stallowner') {
      return 'Stall workspace';
    }

    if (this.role === 'user') {
      return 'Catalogue workspace';
    }

    return 'Simple dashboard';
  }

  Dashboard_Count(role: any) {
    this.service.dashboard_count(role).subscribe(
      (data: any) => {
        this.result = data;
        this.allcount = this.result?.data?.[0] || {};

        this.total_events = this.safeNumber(this.allcount.total_events);
        this.past_events = this.safeNumber(this.allcount.past_events);
        this.current_events = this.safeNumber(this.allcount.current_events);
        this.future_events = this.safeNumber(this.allcount.future_events);
        this.total_users = this.safeNumber(this.allcount.total_users);
        this.event_user = this.safeNumber(this.allcount.event_user);
        this.total_admin = this.safeNumber(this.allcount.total_admin);

        this.registrations = this.safeNumber(this.allcount.registrations);
        this.checkins = this.safeNumber(this.allcount.checkins);
        this.checkouts = this.safeNumber(this.allcount.checkouts);
        this.pending_registrations = this.safeNumber(this.allcount.pending_registrations);
        this.totalNotVisited = this.safeNumber(this.allcount.total_notvisited);
        this.active_scanning_points = this.safeNumber(this.allcount.active_scanning_points);
        this.active_participants = this.safeNumber(this.allcount.active_participants);
        this.registration = this.allcount.registration ?? 0;
        const registration_trend = this.allcount.registration_trend ?? 0;

        this.scans_by_checkpoint = Array.isArray(this.allcount.checkpoint_attendance) ? this.allcount.checkpoint_attendance : [];
        this.scanner_activity = this.mapScannerActivity(this.allcount.admin_scan_activity);
        this.ticket_category_attendance = this.mapTicketCategoryAttendance(this.allcount.ticket_category_attendance);
        this.checkpoint_category_scans = this.mapCheckpointCategoryScans(this.allcount.checkpoint_category_scans);
        this.filteredScannerActivity = [...this.scanner_activity];
        this.refreshDashboardView();

        this.prepareTicketCategoryAttendanceChart();
        if (this.canViewEventAnalytics) {
          this.loadRegistrationTrend(registration_trend);
        }

        this.lastUpdated = new Date();
        this.isLoading = false;
      },
      (error) => {
        console.error('Error fetching dashboard count:', error);
        this.isLoading = false;
      }
    );
  }

  Get_Setting(event_id: any) {
    if (!event_id || event_id === '0') {
      return;
    }

    this.service.getsetting(event_id).subscribe(
      (data: any) => {
        this.setting_data = data?.data;

        if (this.setting_data && this.setting_data[0]) {
          this.registration = this.setting_data[0].registration;
          this.refreshDashboardView();
        }
      }
    );
  }

  loadRegistrationTrend(data: any) {

    if (!this.hasEventContext) return;

    const trend = data;

    if (trend && trend.length) {
      this.prepareRegistrationChart(trend);
    } else {
      this.registrationTrendData = null;
    }
  }

  prepareRegistrationChart(registrations: any[]) {
    if (!registrations || registrations.length === 0) {
      this.registrationTrendData = null;
      return;
    }

    const labels = registrations.map(r => r.reg_date);
    const counts = registrations.map(r => Number(r.total_regs));

    this.registrationTrendData = {
      labels: labels,
      datasets: [
        {
          label: 'Registrations',
          data: counts,
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37, 99, 235, 0.16)',
          type: 'bar'
        }
      ]
    };
  }

  private refreshDashboardView() {
    this.registrationOpen = this.isRegistrationActive();
    this.registrationStatusLabel = this.registrationOpen ? 'Open' : 'Closed';
    this.approvedRegistrations = Math.max(this.registrations - this.pending_registrations, 0);
    this.totalScannerActions = this.checkins + this.checkouts;

    if (!this.totalNotVisited && this.ticket_category_attendance.length) {
      this.totalNotVisited = this.ticket_category_attendance.reduce((sum, item) => sum + item.not_visited, 0);
    }

    if (this.canViewEventAnalytics) {
      this.summaryMetrics = [
        {
          label: 'Registrations',
          value: this.formatNumber(this.registrations),
          helper: 'Total registered people',
          icon: 'assignment_ind',
          tone: 'primary'
        },
        {
          label: 'Approved',
          value: this.formatNumber(this.approvedRegistrations),
          helper: 'Ready for scanning',
          icon: 'verified_user',
          tone: 'success'
        },
        {
          label: 'Pending',
          value: this.formatNumber(this.pending_registrations),
          helper: 'Waiting for approval',
          icon: 'pending_actions',
          tone: 'warning'
        },
        {
          label: 'Not Visited',
          value: this.formatNumber(this.totalNotVisited),
          helper: 'No scan recorded yet',
          icon: 'person_off',
          tone: 'danger'
        },
        {
          label: 'Check In',
          value: this.formatNumber(this.checkins),
          helper: 'Entry scans completed',
          icon: 'login',
          tone: 'info'
        },
        {
          label: 'Check Out',
          value: this.formatNumber(this.checkouts),
          helper: 'Exit scans completed',
          icon: 'logout',
          tone: 'info'
        },
        // {
        //   label: 'Checkpoints',
        //   value: this.formatNumber(this.active_scanning_points),
        //   helper: 'Scanning points active',
        //   icon: 'qr_code_scanner',
        //   tone: 'primary'
        // },
        {
          label: 'Total Scans',
          value: this.formatNumber(this.totalScannerActions),
          helper: 'All QR scans combined',
          icon: 'qr_code_2',
          tone: 'success'
        }
      ];
    } else if (this.canViewSystemAnalytics) {
      this.summaryMetrics = [
        {
          label: 'Total Events',
          value: this.formatNumber(this.total_events),
          helper: 'All events in the system',
          icon: 'event',
          tone: 'primary'
        },
        {
          label: 'Active Events',
          value: this.formatNumber(this.current_events),
          helper: 'Running right now',
          icon: 'event_available',
          tone: 'success'
        },
        {
          label: 'Upcoming Events',
          value: this.formatNumber(this.future_events),
          helper: 'Scheduled next',
          icon: 'schedule',
          tone: 'warning'
        },
        {
          label: 'Event Admins',
          value: this.formatNumber(this.event_user),
          helper: 'Operator accounts assigned',
          icon: 'admin_panel_settings',
          tone: 'info'
        }
      ];
    } else {
      this.summaryMetrics = [
        {
          label: 'Workspace',
          value: 'Ready',
          helper: 'Use the section below to continue',
          icon: this.role === 'stallowner' ? 'storefront' : 'shopping_bag',
          tone: 'primary'
        }
      ];
    }

    this.prepareCheckpointChart();

  }

  private prepareCheckpointChart() {
    if (!this.scans_by_checkpoint || this.scans_by_checkpoint.length === 0) {
      this.checkpointScansChartData = null;
      return;
    }

    const labels = this.scans_by_checkpoint.map((item: any) => item.point || 'Checkpoint');
    const checkinData = this.scans_by_checkpoint.map((item: any) => this.safeNumber(item.checkins));
    const checkoutData = this.scans_by_checkpoint.map((item: any) => this.safeNumber(item.checkouts));

    this.checkpointScansChartData = {
      labels: labels,
      datasets: [
        {
          label: 'Check In',
          data: checkinData,
          backgroundColor: 'rgba(22, 163, 74, 0.78)'
        },
        {
          label: 'Check Out',
          data: checkoutData,
          backgroundColor: 'rgba(37, 99, 235, 0.78)'
        }
      ]
    };
  }

  private prepareTicketCategoryAttendanceChart() {

    if (!this.ticket_category_attendance || this.ticket_category_attendance.length === 0) {
      this.ticketCategoryAttendanceChartData = null;
      return;
    }
    console.log("CALLED");

    const labels = this.ticket_category_attendance.map(item => item.category);
    const registered = this.ticket_category_attendance.map(item => item.total_reg);
    const checkedIn = this.ticket_category_attendance.map(item => item.checkins);
    const notVisited = this.ticket_category_attendance.map(item => item.not_visited);

    console.log(labels);


    this.ticketCategoryAttendanceChartData = {
      labels: labels,
      datasets: [
        {
          label: 'Registered',
          data: registered,
          backgroundColor: 'rgba(107, 114, 128, 0.45)'
        },
        {
          label: 'Visited',
          data: checkedIn,
          backgroundColor: 'rgba(22, 163, 74, 0.78)'
        },
        {
          label: 'Not Visited',
          data: notVisited,
          backgroundColor: 'rgba(239, 68, 68, 0.74)'
        }
      ]
    };

    console.log(this.ticketCategoryAttendanceChartData);

  }

  private mapScannerActivity(data: any): ScannerActivity[] {
    if (!Array.isArray(data)) {
      return [];
    }

    return data
      .map((item: any) => ({
        user_name: item.user_name || 'Unknown',
        user_mobile: item.user_mobile || '',
        date: item.date || '',
        checkpoint: item.point || 'Unknown',
        totalScans: this.safeNumber(item.total_scans),
        checkins: this.safeNumber(item.checkins),
        checkouts: this.safeNumber(item.checkouts),
        last_scan_at: item.last_scan_at || '--'
      }))
      .sort((a, b) => b.totalScans - a.totalScans);
  }

  applyFilterAndSort() {
    let data = [...this.scanner_activity];

    // SEARCH
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();

      data = data.filter(item =>
        item.user_name?.toLowerCase().includes(term) ||
        item.user_mobile?.toLowerCase().includes(term) ||
        item.checkpoint?.toLowerCase().includes(term) ||
        item.date?.toLowerCase().includes(term)
      );
    }

    // SORT
    if (this.sortColumn) {
      data.sort((a, b) => {
        let valA = a[this.sortColumn];
        let valB = b[this.sortColumn];

        // handle null
        valA = valA ?? '';
        valB = valB ?? '';

        if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    this.filteredScannerActivity = data;
  }

  sortBy(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.applyFilterAndSort();
  }

  private mapTicketCategoryAttendance(data: any): TicketCategoryStat[] {
    if (!Array.isArray(data)) {
      return [];
    }

    return data
      .map((item: any) => {
        const total_reg = this.safeNumber(item.total_reg);
        const checkins = this.safeNumber(item.checkins);
        const checkouts = this.safeNumber(item.checkouts);
        const not_visited = item.not_visited !== undefined
          ? this.safeNumber(item.not_visited)
          : Math.max(total_reg - checkins, 0);

        return {
          category: item.category || 'Uncategorised',
          total_reg: total_reg,
          checkins: checkins,
          checkouts: checkouts,
          not_visited: not_visited,
          visitRate: total_reg ? Math.round((checkins / total_reg) * 100) : 0
        };
      })
      .sort((a, b) => b.total_reg - a.total_reg);
  }

  private mapCheckpointCategoryScans(data: any): CheckpointCategoryStat[] {
    if (!Array.isArray(data)) {
      return [];
    }

    return data
      .map((item: any) => ({
        checkpoint: item.point || 'Unknown',
        category: item.category || 'Uncategorised',
        total_reg: this.safeNumber(item.total_reg),
        checkins: this.safeNumber(item.checkins),
        checkouts: this.safeNumber(item.checkouts),
        not_visited: this.safeNumber(item.not_visited),
        uniqueVisitors: this.safeNumber(item.unique_visitors)
      }))
      .sort((a, b) => b.total_reg - a.total_reg);
  }

  private isRegistrationActive(): boolean {
    return this.registration === 1 || this.registration === 'on' || this.registration === true || this.registration === 'true';
  }

  private safeNumber(value: any): number {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(value);
  }

  // Navigation methods
  onSummaryCardClick(metricLabel: string) {
    switch (metricLabel) {
      case 'Registrations':
        this.router.navigate(['/admin/Registered Users']);
        break;
      case 'Approved':
        this.router.navigate(['/admin/Registered Users']);
        break;
      case 'Pending':
        this.router.navigate(['/admin/Registered Users']);
        break;
      case 'Not Visited':
        this.router.navigate(['/admin/Reports']);
        break;
      case 'Check In':
        this.router.navigate(['/admin/Reports']);
        break;
      case 'Check Out':
        this.router.navigate(['/admin/Reports']);
        break;
      case 'Total Scans':
        this.router.navigate(['/admin/Reports']);
        break;
      case 'Total Events':
        this.router.navigate(['/admin/Events']);
        break;
      case 'Active Events':
        this.router.navigate(['/admin/Events']);
        break;
      case 'Upcoming Events':
        this.router.navigate(['/admin/Events']);
        break;
      case 'Event Admins':
        this.router.navigate(['/admin/Users']);
        break;
    }
  }

  onTicketCategoryClick(category: TicketCategoryStat) {
    this.router.navigate(['/admin/ticket-category'], {
      queryParams: { category: category.category }
    });
  }

  onScannerActivityClick(scanner: ScannerActivity) {
    this.router.navigate(['/admin/user'], {
      queryParams: { user: scanner.user_name, mobile: scanner.user_mobile }
    });
  }
}
